import classNames from 'classnames';
import * as React from 'react';
import type { BaseImage } from '../Picture/Picture';
import { Picture } from '../Picture/Picture';
import { PictureProfiles } from '../Picture/PictureProfiles';
import { PicturePlaceholder } from '~/Partials/Picture/Placeholder/PicturePlaceholder';
import type { BackgroundMediaLayoutVariant } from './BackgroundMediaLayoutVariant';
import type { BackgroundMediaOverlayVariant } from './BackgroundMediaOverlayVariant';
import styles from './BackgroundMediaSection.module.css';

export interface BackgroundMediaSectionProps {
    id?: string;
    image?: BaseImage;
    video?: string;
    caption?: string;
    author?: string;
    overlayVariant?: BackgroundMediaOverlayVariant;
    layout?: BackgroundMediaLayoutVariant;
    className?: string;
    wrapperClassName?: string;
    contentClassName?: string;
    mediaClassName?: string;
}

export const BackgroundMediaSection: React.FC<React.PropsWithChildren<BackgroundMediaSectionProps>> = props => {
    const {
        children,
        id,
        image,
        video,
        caption,
        author,
        className,
        wrapperClassName,
        contentClassName,
        mediaClassName,
        layout = 'default',
        overlayVariant = 'topToBottomGreyGradient',
    } = props;

    const getMediaElement = () => {
        const mediaClass = classNames(styles.Media, mediaClassName);

        if (video) {
            return (
                <div className={mediaClass}>
                    <video src={video} autoPlay playsInline muted loop preload="auto" />
                </div>
            );
        }

        if (image) {
            return <Picture {...image} profile={PictureProfiles.fullWidth} className={mediaClass} />;
        }

        return <PicturePlaceholder className={mediaClass} logoWrapperClassName={styles.PlaceholderLogoWrapper} />;
    };

    const getMediaElementWithCaption = () => {
        const mediaElement = getMediaElement();

        return (
            <figure className={styles.Figure}>
                {mediaElement}
                <figcaption>
                    {caption && <span>{caption}</span>}
                    {author && <span>{author}</span>}
                </figcaption>
            </figure>
        );
    };

    const mediaContent = caption || author ? getMediaElementWithCaption() : getMediaElement();

    return (
        <div
            id={id}
            className={classNames(
                styles.BackgroundMediaSection,
                overlayVariant !== 'transparent' && styles[`Variant--${overlayVariant}`],
                (caption || author) && styles['Variant--withCaption'],
                className
            )}
        >
            <div
                className={classNames(
                    styles.Wrapper,
                    layout === 'grid' && styles[`Wrapper--${layout}`],
                    wrapperClassName
                )}
            >
                <div className={classNames(styles.Content, contentClassName)}>{children}</div>
            </div>
            {mediaContent}
        </div>
    );
};
